import React, { useState } from "react";
import {
  Table,
  Spinner,
} from "react-bootstrap";
import CountryList from "../../CountryList";
import { appleApi } from "../../../services/api";
import { Line } from "react-chartjs-2";
import { FaSearch, FaLayerGroup } from "react-icons/fa";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";

// Registering necessary chart elements
ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const KeywordRelevanceComponent = () => {
  const [keyword, setKeyword] = useState("");
  const [country, setCountry] = useState("");
  const [appsData, setAppsData] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch data based on keyword and country
  const fetchData = async () => {
    if (!keyword || !country) {
      alert("Please provide both keyword and country.");
      return;
    }

    setLoading(true);

    try {
      const { data: result } = await appleApi.get("apple-keyword-relevance/", {
          params: { keyword, country },
        });
      const data = result?.apps ?? [];

      setAppsData(data);

      // Prepare data for the line chart
      const chartLabels = data.map((app) => app.app_name);
      const chartScores = data.map((app) =>
        app.keywords.reduce((acc, keyword) => acc + keyword.score, 0)
      );

      setChartData({
        labels: chartLabels,
        datasets: [
          {
            label: "Relevancy Score",
            data: chartScores,
            borderColor: "rgba(75, 192, 192, 1)",
            backgroundColor: "rgba(75, 192, 192, 0.2)",
            fill: true,
          },
        ],
        options: {
          responsive: true,
          plugins: {
            title: {
              display: true,
              text: "Relevancy Score Comparison (Line Chart)",
            },
          },
          scales: {
            x: {
              title: {
                display: true,
                text: "App Name",
              },
            },
            y: {
              title: {
                display: true,
                text: "Relevancy Score",
              },
            },
          },
        },
      });
    } catch (error) {
      console.error("Error fetching data", error);
      alert("Error fetching data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-100">
        <div className="flex items-center mb-6">
          <FaLayerGroup className="text-blue-600 text-2xl mr-3" />
          <h2 className="text-2xl font-bold text-gray-800">Keyword Relevancy</h2>
        </div>

        {/* Search Section */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Keyword
            </label>
            <input
              type="text"
              className="w-full p-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
              placeholder="Enter keyword"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
          </div>

          <div className="md:col-span-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Country
            </label>
            <CountryList
              onSelect={(selectedCountry) => setCountry(selectedCountry)}
            />
          </div>

          <div className="md:col-span-2 flex items-end">
            <button
              onClick={fetchData}
              disabled={loading}
              className={`w-full bg-blue-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center ${
                loading ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="mr-2" />
                  Searching...
                </>
              ) : (
                <>
                  <FaSearch className="mr-2" /> Search
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center items-center h-64">
          <Spinner animation="border" role="status" className="text-blue-600" />
          <span className="ml-3 text-lg text-gray-600 font-medium">Loading... Please Wait</span>
        </div>
      )}

      {!loading && appsData.length > 0 && (
        <div className="mt-8">
          <div className="mb-8">
            {chartData && (
              <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Relevancy Score Comparison</h3>
                <Line data={chartData} options={chartData.options} />
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
            <Table responsive hover className="mb-0">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">App Name</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Keywords</th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Score</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {appsData.map((app) => (
                  <React.Fragment key={app.app_id}>
                    <tr>
                      <td rowSpan={app.keywords.length} className="px-6 py-4 whitespace-nowrap align-middle">
                        <a
                          href={`https://apps.apple.com/us/app/${app.app_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 font-semibold no-underline"
                        >
                          {app.app_name}
                        </a>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{app.keywords[0].keyword}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {app.keywords[0].score.toFixed(2)}
                        </span>
                      </td>
                    </tr>
                    {app.keywords.slice(1).map((kw, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap">{kw.keyword}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {kw.score.toFixed(2)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};

export default KeywordRelevanceComponent;

